// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renders Contract Review document rows and optional connector-owned groups.
 *
 * Group headers add navigation and bulk selection without replacing the
 * document row contract: every attachment keeps its own review history,
 * download access, and explicit agent-run controls. Connector provenance owns
 * version changes and removal, but never owns analysis launch.
 * Callers decide which source groups are expanded so filtering and pagination
 * remain table-level concerns.
 */
import { Fragment } from 'react'
import { ChevronDown, ChevronRight, Download, Files, History, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react'

import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { SourceProviderBadge } from '@/components/SourceProviderBadge'
import type { Agent } from '@/subsystems/agents'
import type { SourceDisplayField } from '@/subsystems/documents'

import type { AgentRunHistory, WorkspaceAgentRuns } from '../hooks/useWorkspaceAgentRuns'
import type { DocumentListItemWithPipelines } from '../models'
import { agentAcceptsDocuments } from '../lib/agentRunRules'
import { documentsInEntry } from '../lib/workspaceDocumentGroups'
import type { WorkspaceDocumentEntry } from '../lib/workspaceDocumentGroups'
import { LatestRuns, RunAgentsMenu } from './WorkspaceRunControls'

interface WorkspaceDocumentRowsProps {
  entries: WorkspaceDocumentEntry[]
  externalIdLabel: string | null
  selected: Set<string>
  expandedGroups: Set<string>
  forceExpandGroups: boolean
  agents: Agent[]
  runHistory: WorkspaceAgentRuns
  runningAgentIdsFor: (document: DocumentListItemWithPipelines) => Set<string>
  onToggleDocument: (documentId: string) => void
  onToggleDocuments: (documents: DocumentListItemWithPipelines[]) => void
  onToggleGroup: (groupKey: string) => void
  onRun: (agentIds: string[], documentIds: string[]) => void
  onView: (document: DocumentListItemWithPipelines, run: AgentRunHistory, version: number) => void
  onDownload: (document: DocumentListItemWithPipelines) => void
  onManageVersions: (documentId: string) => void
  onDelete: (document: DocumentListItemWithPipelines) => void
}

interface DocumentRowProps extends Omit<WorkspaceDocumentRowsProps,
  'entries' | 'expandedGroups' | 'forceExpandGroups' | 'onToggleDocuments' | 'onToggleGroup'> {
  document: DocumentListItemWithPipelines
  nested: boolean
}

function SourceDisplayFields({ fields }: { fields: SourceDisplayField[] }) {
  if (fields.length === 0) return null
  return (
    <div className="mt-0.5 flex min-w-0 flex-wrap gap-x-2 text-[11px] text-muted-foreground">
      {fields.map(field => (
        <span key={field.key} className="min-w-0 break-words">
          {field.label}: {field.value}
        </span>
      ))}
    </div>
  )
}

function DocumentRow({
  document,
  nested,
  externalIdLabel,
  selected,
  agents,
  runHistory,
  runningAgentIdsFor,
  onToggleDocument,
  onRun,
  onView,
  onDownload,
  onManageVersions,
  onDelete,
}: DocumentRowProps) {
  const connectorOwned = document.sourceProvider !== null
  const runningAgentIds = runningAgentIdsFor(document)
  const disabledAgentIds = new Set(runningAgentIds)
  for (const agent of agents) {
    if (!agentAcceptsDocuments(agent, [document])
      || (agent.pipelineId === 'version-diff' && document.latestVersion < 2)) {
      disabledAgentIds.add(agent.agentId)
    }
  }

  return (
    <tr className="border-t border-border" data-testid={`cr-doc-row-${document.documentId}`}>
      <td className="px-3 py-2">
        <Checkbox
          aria-label={`Select ${document.documentId}`}
          checked={selected.has(document.documentId)}
          onCheckedChange={() => onToggleDocument(document.documentId)}
        />
      </td>
      <td className={`min-w-0 px-3 py-2 ${nested ? 'pl-12' : ''}`}>
        <div className="break-words font-medium text-slate-900">{document.latestFilename ?? document.name}</div>
        <div className="mt-0.5 break-words font-mono text-[11px] text-muted-foreground">
          v{document.latestVersion} · {document.documentId}
        </div>
      </td>
      {externalIdLabel ? (
        <td className="min-w-0 px-3 py-2 text-xs">
          <div className="min-w-0">
            <span className="block break-words font-mono">{document.externalId ?? '—'}</span>
            <SourceDisplayFields fields={document.sourceDisplayFields} />
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {document.recordType ? (
                <Badge variant="secondary" className="h-5 text-[10px] font-medium">
                  {document.recordType}
                </Badge>
              ) : null}
              {document.artifactType ? (
                <Badge variant="outline" className="h-5 capitalize text-[10px] font-medium">
                  {document.artifactType}
                </Badge>
              ) : null}
              <SourceProviderBadge provider={document.sourceProvider} />
            </div>
          </div>
        </td>
      ) : null}
      <td className="hidden break-words px-3 py-2 xl:table-cell">
        <div>{document.uploadedBy ?? '—'}</div>
        <div className="text-[11px] text-muted-foreground">
          {document.latestVersionAt ? new Date(document.latestVersionAt).toLocaleString() : '—'}
        </div>
      </td>
      <td className="hidden px-3 py-2 lg:table-cell" data-testid={`cr-latest-run-${document.documentId}`}>
        <LatestRuns
          runs={runHistory.get(document.documentId) ?? []}
          runningAgentIds={runningAgentIds}
          onView={(run, version) => onView(document, run, version)}
          onRetry={(run) => onRun([run.agentId], [document.documentId])}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center justify-end gap-1" data-testid={`cr-run-actions-${document.documentId}`}>
          <RunAgentsMenu
            agents={agents}
            disabledAgentIds={disabledAgentIds}
            runningAgentIds={runningAgentIds}
            onRun={(agentIds) => onRun(agentIds, [document.documentId])}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`More actions for ${document.documentId}`}><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownload(document)}><Download className="mr-2 h-4 w-4" /> Download</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onManageVersions(document.documentId)}><History className="mr-2 h-4 w-4" /> Version history</DropdownMenuItem>
              {connectorOwned ? (
                <div className="flex max-w-64 items-start gap-2 border-t px-2 py-2 text-[11px] leading-4 text-slate-400">
                  <RefreshCw className="mt-0.5 h-3 w-3 shrink-0" />
                  Versions come from sync. Run analysis here; manage removal from Connectors.
                </div>
              ) : (
                <DropdownMenuItem className="text-red-600" onClick={() => onDelete(document)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  )
}

export function WorkspaceDocumentRows(props: WorkspaceDocumentRowsProps) {
  const columnCount = props.externalIdLabel ? 6 : 5

  return props.entries.map((entry) => {
    if (entry.kind === 'document') {
      return <DocumentRow key={entry.key} {...props} document={entry.document} nested={false} />
    }

    const expanded = props.forceExpandGroups || props.expandedGroups.has(entry.key)
    const groupDocuments = documentsInEntry(entry)
    const selectedCount = groupDocuments.filter(
      document => props.selected.has(document.documentId),
    ).length
    const allSelected = selectedCount === groupDocuments.length
    return (
      <Fragment key={entry.key}>
        <tr
          className="border-t border-border bg-slate-50/80"
          data-testid={`cr-doc-group-${entry.groupKey}`}
        >
          <td className="px-3 py-2.5">
            <Checkbox
              aria-label={`Select ${entry.groupName}`}
              checked={allSelected ? true : selectedCount > 0 ? 'indeterminate' : false}
              onCheckedChange={() => props.onToggleDocuments(groupDocuments)}
            />
          </td>
          <td colSpan={columnCount - 1} className="px-3 py-2.5">
            <button
              type="button"
              className="flex w-full items-center gap-2 text-left"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${entry.groupName}`}
              onClick={() => props.onToggleGroup(entry.key)}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Files className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{entry.groupName}</span>
                  <SourceProviderBadge provider={entry.sourceProvider} />
                </div>
                <SourceDisplayFields fields={entry.sourceDisplayFields} />
              </div>
              <span className="ml-auto text-xs text-muted-foreground">
                {entry.recordTypes.length} {entry.recordTypes.length === 1 ? 'record type' : 'record types'} ·{' '}
                {groupDocuments.length} {groupDocuments.length === 1 ? 'document' : 'documents'}
              </span>
            </button>
          </td>
        </tr>
        {expanded ? entry.recordTypes.map((recordType) => {
          const typeExpanded = props.forceExpandGroups || props.expandedGroups.has(recordType.key)
          const typeSelectedCount = recordType.documents.filter(
            document => props.selected.has(document.documentId),
          ).length
          const allTypeSelected = typeSelectedCount === recordType.documents.length
          return (
            <Fragment key={recordType.key}>
              <tr className="border-t border-border bg-slate-50/40">
                <td className="px-3 py-2 pl-7">
                  <Checkbox
                    aria-label={`Select ${recordType.recordType}`}
                    checked={allTypeSelected ? true : typeSelectedCount > 0 ? 'indeterminate' : false}
                    onCheckedChange={() => props.onToggleDocuments(recordType.documents)}
                  />
                </td>
                <td colSpan={columnCount - 1} className="px-3 py-2 pl-8">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 text-left"
                    aria-label={`${typeExpanded ? 'Collapse' : 'Expand'} ${recordType.recordType}`}
                    onClick={() => props.onToggleGroup(recordType.key)}
                  >
                    {typeExpanded
                      ? <ChevronDown className="h-4 w-4" />
                      : <ChevronRight className="h-4 w-4" />}
                    <Badge variant="secondary">{recordType.recordType}</Badge>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {recordType.documents.length}{' '}
                      {recordType.documents.length === 1 ? 'document' : 'documents'}
                    </span>
                  </button>
                </td>
              </tr>
              {typeExpanded ? recordType.documents.map(document => (
                <DocumentRow key={document.documentId} {...props} document={document} nested />
              )) : null}
            </Fragment>
          )
        }) : null}
      </Fragment>
    )
  })
}
